import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import useConceptBuilder from '../hooks/useConceptBuilder';
import { subscribeProgressBus } from '../utils/progressBus';

const ConceptBuilderContext = createContext(null);

function normalizeSurfaceContext(value) {
  const input = value || {};
  return {
    surface: (input.surface || 'none').toLowerCase(),
    deck_id: input.deck_id || input.deckId || null,
    subtopic_id: input.subtopic_id || null,
    drug_id: input.drug_id || null,
    block_id: input.block_id || null,
  };
}

export function ConceptBuilderProvider({ children }) {
  const builder = useConceptBuilder(null);
  const {
    active,
    setActive,
    selections,
    setSelections,
    addSelection,
    removeSelection,
    clearSelections,
  } = builder;
  const [builderModalOpen, setBuilderModalOpen] = useState(false);
  // Lifted from ConceptCardCarousel so the BLS wand menu can trigger them.
  const [vaultOpen, setVaultOpen] = useState(false);
  const [cheatSheetEditor, setCheatSheetEditor] = useState(null);
  const [lastCardSavedAt, setLastCardSavedAt] = useState(0);
  // Bumps whenever a mastery-fanout API call returns success. Carousels
  // and the ObjectiveView objective-scoped grid include this in their
  // useEffect deps so they refetch on live progress changes without
  // requiring a page reload.
  const [lastProgressBump, setLastProgressBump] = useState(0);
  const [surfaceContext, setSurfaceContextState] = useState(() => normalizeSurfaceContext(null));

  const notifyCardSaved = useCallback(() => {
    setLastCardSavedAt(Date.now());
  }, []);

  useEffect(
    () => subscribeProgressBus(() => setLastProgressBump(Date.now())),
    [],
  );

  const setSurfaceContext = useCallback((nextContext) => {
    setSurfaceContextState((prev) => {
      const normalized = normalizeSurfaceContext(nextContext);
      if (
        prev.surface === normalized.surface &&
        prev.deck_id === normalized.deck_id &&
        prev.subtopic_id === normalized.subtopic_id &&
        prev.drug_id === normalized.drug_id &&
        prev.block_id === normalized.block_id
      ) {
        return prev;
      }
      return normalized;
    });
  }, []);

  const contextValue = useMemo(() => ({
    active,
    setActive,
    selections,
    setSelections,
    addSelection,
    removeSelection,
    clearSelections,
    builderModalOpen,
    setBuilderModalOpen,
    vaultOpen,
    setVaultOpen,
    cheatSheetEditor,
    setCheatSheetEditor,
    lastCardSavedAt,
    notifyCardSaved,
    lastProgressBump,
    surfaceContext,
    setSurfaceContext,
  }), [
    active,
    setActive,
    selections,
    setSelections,
    addSelection,
    removeSelection,
    clearSelections,
    builderModalOpen,
    setBuilderModalOpen,
    vaultOpen,
    setVaultOpen,
    cheatSheetEditor,
    setCheatSheetEditor,
    lastCardSavedAt,
    notifyCardSaved,
    lastProgressBump,
    surfaceContext,
    setSurfaceContext,
  ]);

  return (
    <ConceptBuilderContext.Provider value={contextValue}>
      {children}
    </ConceptBuilderContext.Provider>
  );
}

export function useGlobalConceptBuilder() {
  const ctx = useContext(ConceptBuilderContext);
  if (!ctx) throw new Error('useGlobalConceptBuilder must be used inside ConceptBuilderProvider');
  return ctx;
}
